// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React from "react";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
// import ProductPartNumbers, { ProductPartNumbersStyles } from "@store/Overrides/Components/ProductPartNumbers"; // Store Custom Code
import { autocompleteLinkWithImageStyles } from "@store/Themes/common/mixins/autocomplete";
// import { autocompleteProps } from "@store/Themes/common/props/autocomplete"; // Store Custom Code
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";

/* Store Custom Code - Start */
import {
    calculateProductsAdditionalCustomerProductNames,
    getProductMultiLocationCatalogResultMap,
} from "@store/Framework/Common/Utilities/multiLocationCatalogUtilities";
import AvailableAtOtherShipToLabel, {
    AvailableAtOtherShipToLabelPresentationProps,
} from "@store/Library/Components/MultiLocationCatalog/AvailableAtOtherShipToLabel";
import NotInYourCatalogBadge, {
    NotInYourCatalogBadgePresentationProps,
} from "@store/Library/Components/NotInYourCatalogBadge";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@store/Overrides/Components/ProductPartNumbers";
import { autocompleteProps } from "@store/Themes/common/props/autocomplete";
/* Store Custom Code - End */
import getBooleanProperty from "@toolkit/Framework/Common/Utilities/getBooleanProperty";

const StyledSpan = getStyledWrapper("span");

const linkWithImageProps: LinkPresentationProps = {
    typographyProps: { css: autocompleteLinkWithImageStyles() },
};
/* Starter Custom Code - End */

interface Props {
    products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    // Starter Custom Code
    focusedLink?: LinkPresentationProps;
    imageWrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    productPartNumbers?: ProductPartNumbersStyles;
    // Store Custom Code
    availableAtOtherShipToLabel?: AvailableAtOtherShipToLabelPresentationProps;
    notInYourCatalogBadge?: NotInYourCatalogBadgePresentationProps;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: autocompleteProps.headerText,
    imageWrapper: autocompleteProps.imageWrapper,
    infoWrapper: autocompleteProps.infoWrapper,
    titleLink: mergeProps<AutocompleteProductsStyles["titleLink"]>(autocompleteProps.link, linkWithImageProps),
    focusedLink: mergeProps<AutocompleteProductsStyles["focusedLink"]>(
        autocompleteProps.focusedLink,
        linkWithImageProps,
    ),
    title: autocompleteProps.title,
    erpNumberText: autocompleteProps.erpNumber,
    productPartNumbers: autocompleteProps.productPartNumbers,
    /* Store Custom Code - Start */
    availableAtOtherShipToLabel: autocompleteProps.availableAtOtherShipToLabel,
    notInYourCatalogBadge: autocompleteProps.notInYourCatalogBadge,
    /* Store Custom Code - End */
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products } = this.props;
        const styles = this.styles;

        /* Store Custom Code - Start */
        const multiLocationCatalogResults = getProductMultiLocationCatalogResultMap(products);
        const productsAdditionalCustomerProductNames = calculateProductsAdditionalCustomerProductNames(
            products,
            multiLocationCatalogResults,
        );
        /* Store Custom Code - End */

        return (
            <>
                <Typography
                    {...styles.headerText}
                    data-test-selector="autocompleteProducts_Header"
                    className="autocomplete-products-header"
                >
                    {translate("Products")}
                </Typography>
                {/* Starter Custom Code - Start */}
                <ul className="autocomplete-products-container">
                    {products.map(product => {
                        /* Store Custom Code - Start */
                        const isMultilocationCatalogProduct = !!multiLocationCatalogResults.get(product.id ?? "")
                            ?.isMultiLocationCatalogProduct;
                        const isProductNotInCatalog = getBooleanProperty(product.properties?.notInCatalog);
                        const showNotInYourCatalogBadge = isProductNotInCatalog && !isMultilocationCatalogProduct;
                        const additionalCustomerProductNames = productsAdditionalCustomerProductNames.get(
                            product.id ?? "",
                        );
                        /* Store Custom Code - End */

                        return (
                            <li key={product.id!} className="autocomplete-products-item">
                                <Link
                                    {...(this.props.focusedItem === product ? styles.focusedLink : styles.titleLink)}
                                    onClick={() => {
                                        this.props.goToUrl(product.url);
                                    }}
                                    className="autocomplete-products-link"
                                >
                                    <StyledSpan {...styles.imageWrapper} aria-hidden={true}>
                                        <LazyImage
                                            {...styles.image}
                                            src={product.image}
                                            className="autocomplete-products-image"
                                            altText={product.title}
                                        />
                                    </StyledSpan>
                                    <StyledSpan {...styles.infoWrapper} className="autocomplete-products-info">
                                        {/* Store Custom Code - Start */}
                                        {isMultilocationCatalogProduct && (
                                            <AvailableAtOtherShipToLabel {...styles.availableAtOtherShipToLabel} />
                                        )}
                                        {showNotInYourCatalogBadge && (
                                            <NotInYourCatalogBadge {...styles.notInYourCatalogBadge} />
                                        )}
                                        {/* Store Custom Code - End */}
                                        <Typography
                                            {...styles.title}
                                            data-test-selector={`productId_${product.id}`}
                                            className="autocomplete-products-title"
                                        >
                                            {product.displayTitle}
                                        </Typography>
                                        <Typography
                                            {...styles.erpNumberText}
                                            data-test-selector="autocompleteProducts_PartNumber"
                                            className="autocomplete-products-erp-number"
                                        >
                                            <ProductPartNumbers
                                                productNumber={product.displayErpNumber}
                                                customerProductNumber={product.displayName}
                                                manufacturerItem={product.displayManufacturerItemNumber}
                                                showCustomerName={!!product.isNameCustomerOverride}
                                                extendedStyles={styles.productPartNumbers}
                                                // Store Custom Code
                                                additionalCustomerProductNumberCount={
                                                    additionalCustomerProductNames?.additionalCount
                                                }
                                            />
                                        </Typography>
                                    </StyledSpan>
                                </Link>
                            </li>
                        );
                    })}
                </ul>
                {/* Starter Custom Code - End */}
            </>
        );
    }
}

export default AutocompleteProducts;
